import { SEARCH_FLAGS } from '@/constants/experimentConstants';
import useViewToggle from '@/features/searchV2/components/ViewToggle/ViewToggle';
import { SEARCH_RESULT_PER_PAGE_LIMIT } from '@/features/searchV2/constants';
import {
  getSearchPerformedTrackingProps,
  locationSearchContext,
  trackSearchPerformed,
} from '@/features/searchV2/helpers/tracking';
import { SearchContext as TSearchContext } from '@/features/searchV2/types/tracking';
import { locationSlug, slug } from '@/helpers/urlTs';
import {
  getKeywordWithSlug,
  getLocationWithSlug,
  getMatchingKeyword,
  getMatchingLocation,
} from '@/hooks/search/useSearch.helpers';
import { SEARCH_STATE_TIMESTAMP } from '@/hooks/search/useSearchStateCache';
import { useGetAmplitudeExperimentVariant } from '@/hooks/useAmplitudeExperiment';
import { SEARCH_ACTION, SearchState } from '@/reducers/searchV2Reducer';
import { placeService } from '@/services';
import { searchFeatureFlags as searchFeatureFlagsType, UrlSearchState } from '@/types/state/search';
import debounce from 'lodash.debounce';
import { createContext, useContext, useEffect, useRef } from 'react';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '..';
import {
  DEFAULT_MY_LOCATION_KEYWORD,
  DEFAULT_SEARCH_KEYWORD,
  DEFAULT_SEARCH_LOCATION,
  SEARCH_PAGE_VIEWS,
} from './useSearch.constants';
import {
  buildServerRequestSearchParams,
  buildUrlSearchParams,
  collectBiddingBidIds,
  getSearchUrlParameters,
  requestAndSetExtendedSearchResults,
  requestAndSetSearchResults,
  SearchType,
  shouldSyncClientSearchQuery,
  trackSearchResultsCustomerIO,
  withNearMeDefaultSort,
} from './useSearch.helpers';
import { BaseSearchManagerOutput } from './useSearch.types';
type UseSearchManagerResult = ReturnType<typeof useSearchManager>;

export type SearchManagerOutput = BaseSearchManagerOutput & {
  places: SearchState['places'];
  topSearch: SearchState['topSearch'];
  results: number;
  debugLocation: SearchState['debugLocation'];
  performSyncedSearch: () => void;
  /**
   * @override performSearch
   * when we refactor to use useSearchManagerCore, we want to keep the performSearch signature the same, but for now we need to support both the old and new signature for backward compatibility
   */
  performSearch: (
    params: UrlSearchState,
    options?: {
      extendedSearch?: boolean;
      bounds?: string;
      keywordSubmitted?: boolean;
      searchContext?: TSearchContext;
    },
    onCachedResults?: () => void,
  ) => void;
};

export type SearchManagerProps = {
  type: SearchType;
};

const DEBOUNCE_SEARCH_REQUEST_MS = 500;

/**
 * Change the URL to sync url with search state
 */
export function changeUrl(
  {
    type,
    what,
    where,
    searchQuery,
    isIndexable,
    searchContext,
  }: {
    type: SearchType;
    what: string;
    where: string;
    searchQuery?: string;
    isIndexable?: boolean;
    searchContext?: TSearchContext;
  },
  history,
  location,
) {
  let pathname;
  if (isIndexable) {
    pathname = `${type === 'sistaminuten' ? '/sistaminuten' : ''}`;
    if (!what && !where && type === 'sistaminuten') {
    } else {
      what = (what || '').trim() || DEFAULT_SEARCH_KEYWORD;
      where = (where || '').trim() || DEFAULT_SEARCH_LOCATION;
      pathname += `/${slug(what)}/${locationSlug(where)}`;
    }
  } else {
    pathname = `${type === 'sistaminuten' ? '/sistaminuten-sok' : '/sok'}`;
  }
  // The new entry carries this search's context, not the one of the link that opened the page: the page's URL sync
  // can re-run the search for this entry and reads the context from it.
  history.push({ pathname, search: searchQuery, state: { ...location?.state, searchContext } });
}

const useSearchManager = ({ type }: SearchManagerProps): SearchManagerOutput => {
  const searchState = useAppSelector((state) => state.searchV2);
  const dispatcher = useAppDispatch();
  const match = useRouteMatch() as { params?: { q?: string; location?: string } } | null;
  const history = useHistory();
  const location = useLocation<{ searchContext?: TSearchContext; keywordSubmitted?: boolean }>();
  const urlState = getSearchUrlParameters({ match });

  // update page from Redux set by server during SSR
  urlState.page = urlState.page ?? searchState.ssrRenderedPage ?? 0;

  const viewToggle = useViewToggle({
    views: [...SEARCH_PAGE_VIEWS],
    initialState: SEARCH_PAGE_VIEWS[0],
    className: 'px-sm',
  });
  const flags = useAppSelector((state) => state.flags);
  const getVariant = useGetAmplitudeExperimentVariant();

  // search flags
  let searchFlagsToSend: searchFeatureFlagsType = {};
  SEARCH_FLAGS.forEach((flag) => {
    let value = undefined;
    if ((value = flags[flag]?.value ?? getVariant(flag)?.value ?? undefined)) {
      searchFlagsToSend[flag] = value.toString();
    }
  });

  const handleChangeUrl = (
    what = DEFAULT_SEARCH_KEYWORD,
    where = DEFAULT_SEARCH_LOCATION,
    searchQuery: string,
    isIndexable: boolean,
    searchContext?: TSearchContext,
  ) => changeUrl({ type, what, where, searchQuery, isIndexable, searchContext }, history, location);

  /**
   * Perform search on mount if not the search results are already fetched
   * on SSR
   */
  useEffect(() => {
    // Read before the replace below, which changes the history action.
    const searchContext = locationSearchContext(history, location?.state) ?? 'free_search';
    const keywordSubmitted = location?.state?.keywordSubmitted === true;
    if (keywordSubmitted) {
      history.replace({
        ...location,
        state: { ...location.state, keywordSubmitted: undefined },
      });
    }

    const sessionFlag = sessionStorage.getItem(SEARCH_STATE_TIMESTAMP);
    if (sessionFlag) {
      return;
    }

    if (!searchState.isSSRFetched) {
      performSearch(urlState, {
        keywordSubmitted,
        searchContext,
      });
    } else {
      // SSR renders the first SERP server-side and skips performSearch, so this is the only place
      // a server-rendered rocket slot can be reported. The slot itself only renders on page 0.
      if (type !== 'sistaminuten' && (searchState.ssrRenderedPage ?? 0) === 0) {
        const ssrBidIds = collectBiddingBidIds(searchState.topSearch);
        if (ssrBidIds.length > 0) {
          placeService.storeImpressions(ssrBidIds);
        }
      }

      // if search results are already fetched, send a page view event
      trackSearchResultsCustomerIO(urlState, searchState);

      trackSearchPerformed(
        getSearchPerformedTrackingProps({
          urlState: withNearMeDefaultSort(type, urlState),
          searchState,
          context: searchContext,
          screenName: type === 'sistaminuten' ? 'smt_search_results' : 'search_results',
        }),
      );
      // As after a fetched search, the list a back/forward shows continues as `free_search`, not as the server's
      // `url_entry`.
      if (searchContext === 'navigation') {
        dispatcher({ type: SEARCH_ACTION.SET_SEARCH_CONTEXT, payload: 'free_search' });
      }
    }
    return () => {
      dispatcher({ type: SEARCH_ACTION.SET_RESET_RESULTS });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchPerformed = () => {};

  /**
   * wait for all changes to be done before triggering search to avoid multiple requests
   */
  const debouncedSearch = useRef(
    debounce(
      (
        params: UrlSearchState,
        state: SearchState,
        resetCache: boolean,
        searchContext: TSearchContext,
        bounds: string,
        keywordSubmitted?: boolean,
      ) => {
        requestAndSetSearchResults(
          {
            type,
            view: viewToggle.view,
            params,
            state,
            bounds,
            keywordSubmitted,
            searchContext,
            resetAllSearchItems: resetCache,
          },
          dispatcher,
          handleSearchPerformed,
        );
      },
      DEBOUNCE_SEARCH_REQUEST_MS,
    ),
  ).current;

  /**
   * Navigation initiated Search request (forces a new search)
   * and skips updating the url state (only used for navigation and not user initiated search)
   */
  function performSyncedSearch() {
    const searchParams = buildServerRequestSearchParams(urlState, urlState, searchState);
    debouncedSearch(
      { ...searchParams, q: urlState.q, location: urlState.location },
      searchState,
      true,
      locationSearchContext(history, location?.state),
    );
  }

  function performSearch(
    params: UrlSearchState,
    options: {
      extendedSearch?: boolean;
      bounds?: string;
      keywordSubmitted?: boolean;
      searchContext?: TSearchContext;
    } = {},
    onCachedResults?: () => void,
  ) {
    const q = (() => {
      if ('q' in params) {
        return params.q;
      }
      return urlState.q;
    })();

    const location = (() => {
      if ('location' in params) {
        return params.location;
      }
      return urlState.location;
    })();
    const page = (() => {
      if ('page' in params) {
        return params.page;
      }

      return urlState.page;
    })();

    const sort = (() => {
      if ('sort' in params) {
        return params.sort;
      }
      return urlState.sort;
    })();

    const explain = (() => {
      if ('explain' in params) {
        return params.explain;
      }
      return urlState.explain;
    })();

    const updatedKeyword = q !== urlState.q;
    const updatedLocation = location !== urlState.location;

    const indexableKeyword = getKeywordWithSlug(q);
    const indexableLocation = getLocationWithSlug(location);
    let isSearchIndexable;
    let updatedSlugs = false;
    if (!(indexableKeyword?.slug && indexableLocation?.slug)) {
      isSearchIndexable = false;
    } else {
      isSearchIndexable = true;
      if (
        (!updatedKeyword && indexableKeyword.slug !== match?.params?.q) ||
        (!updatedLocation && indexableLocation.slug !== match?.params?.location)
      ) {
        updatedSlugs = true;
      }
    }

    const updatedPage = page !== urlState.page;

    const updatedSearchQuery =
      shouldSyncClientSearchQuery(params, urlState) || isSearchIndexable !== searchState.isIndexable;

    const updatedBounds = options.bounds !== undefined && options.bounds !== searchState.bounds;

    /**
     * If keyword, location or search query or bounds is updated, but page param is not
     * then we need to reset the page to 0 and perform a new search (i.e not use cached results)
     */
    const resetSearch = (updatedKeyword || updatedLocation || updatedSearchQuery || updatedBounds) && !updatedPage;

    if (resetSearch) {
      params['page'] = null;
    }

    /**
     * If location is updated and is not set to currentLocation or sorting preference is set
     * to "closest", remove the lat and lon params
     */
    if (updatedLocation && locationSlug(location) !== DEFAULT_MY_LOCATION_KEYWORD && sort !== 'closest') {
      params['lat'] = null;
      params['lon'] = null;
    }

    if (explain) {
      params['explain'] = explain || undefined;
    }

    if (Object.keys(searchFlagsToSend).length) {
      params['searchFeatureFlags'] = searchFlagsToSend;
    }

    /**
     * Check if search contains any search params, and if any new search params are added that needs sync
     * if key is included in params then it should override the urlState
     */
    const searchParams = buildServerRequestSearchParams(params, urlState, searchState);
    const urlSearchParams = buildUrlSearchParams(params, urlState, isSearchIndexable);
    if (updatedKeyword || updatedLocation || updatedSearchQuery || resetSearch || updatedSlugs) {
      const searchQuery = urlSearchParams.toString() ?? window.location.search.replace('?', '');
      handleChangeUrl(q, location, searchQuery, isSearchIndexable, options.searchContext);
    }

    if (options.extendedSearch) {
      requestAndSetExtendedSearchResults({ type, params: urlState, state: searchState }, dispatcher);
      return;
    }

    if (options.bounds) {
      requestAndSetSearchResults(
        {
          type,
          view: viewToggle.view,
          params: urlState,
          state: searchState,
          resetAllSearchItems: resetSearch,
          bounds: options.bounds,
          keywordSubmitted: options.keywordSubmitted,
          searchContext: options.searchContext,
        },
        dispatcher,
        handleSearchPerformed,
      );
      return;
    }

    const tryGetCachedResults = !resetSearch;

    if (tryGetCachedResults) {
      const currentPage = Math.max(0, Number(urlState.page) || 0);
      /**
       * if params.page is set, use that, otherwise use the current page
       * since the page is not being changed
       */
      const newPage = (() => {
        if ('page' in params) {
          return params.page || 0;
        } else {
          return currentPage;
        }
      })();

      const cachedResults = searchState.places?.[newPage] || [];

      if (cachedResults.length) {
        onCachedResults?.();
        return;
      }
    }

    placeService.removeStoredPlaceImpressions();

    debouncedSearch(
      { ...searchParams, q, location },
      searchState,
      resetSearch,
      options.searchContext,
      options.bounds,
      options.keywordSubmitted,
    );
  }

  function paginate({ onCachedResults }: { onCachedResults?: () => void }) {
    const { extendSearchIteration, results, searchContext } = searchState;
    return {
      nextPage: () => {
        const currentPage = Math.max(0, Number(urlState.page) || 0);
        const extendedSearch = Boolean(extendSearchIteration) && results <= currentPage * SEARCH_RESULT_PER_PAGE_LIMIT;
        performSearch({ page: currentPage + 1 }, { extendedSearch, searchContext }, onCachedResults);
      },
      prevPage: () => {
        const currentPage = Math.max(0, Number(urlState.page) || 0);
        const page = currentPage > 1 ? currentPage - 1 : null;
        performSearch({ page }, { extendedSearch: Boolean(extendSearchIteration), searchContext }, onCachedResults);
      },
    };
  }

  return {
    q: urlState.q?.toLowerCase() !== DEFAULT_SEARCH_KEYWORD ? getMatchingKeyword(urlState.q) : '',
    location:
      urlState.location?.toLowerCase() !== DEFAULT_SEARCH_LOCATION && urlState.location?.toLowerCase() !== 'var'
        ? getMatchingLocation(urlState.location)
        : '',
    startDate: urlState.startDate,
    endDate: urlState.endDate,
    timeOfDay: urlState.timeOfDay,
    prefs: urlState.prefs,
    sort: urlState.sort,
    lat: urlState.lat,
    lon: urlState.lon,
    page: urlState?.page || 0,
    places: searchState?.places || [],
    topSearch: searchState?.topSearch ?? { places: [] },
    results: searchState?.results || 0,
    fetching: searchState?.fetching || false,
    searchContext: searchState.searchContext,
    type,
    performSearch,
    performSyncedSearch,
    paginate,
    viewToggle,
    debugLocation: searchState?.debugLocation || undefined,
    isIndexable: searchState.isIndexable,
  };
};

export const SearchContext = createContext<UseSearchManagerResult>({
  type: 'default',
  q: '',
  location: '',
  startDate: '',
  endDate: '',
  timeOfDay: undefined,
  places: [],
  topSearch: { places: [] },
  results: 0,
  fetching: false,
  page: 0,
  prefs: undefined,
  sort: undefined,
  lat: undefined,
  lon: undefined,
  searchContext: undefined,
  performSearch: () => {},
  performSyncedSearch: () => {},
  paginate: () => ({ nextPage: () => {}, prevPage: () => {} }),
  viewToggle: {
    view: 'list',
    setView: () => {},
    render: null,
  },
  debugLocation: undefined,
  isIndexable: true,
});

export const useSearchContext = (): UseSearchManagerResult => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error('useSearchContext must be used within a SearchProvider');
  }
  return context;
};

export const SearchProvider: React.FC<{
  type: SearchManagerProps['type'];
  children: React.ReactNode;
}> = ({ type, children }) => {
  const manager = useSearchManager({ type });
  return <SearchContext.Provider value={manager}>{children}</SearchContext.Provider>;
};
